import { Input } from '@mui/joy';

import { ChevronDownIcon } from 'lucide-react';
import { FormEvent, useCallback, useState } from 'react';

import { DropdownMenu, DropdownMenuTrigger } from '@/components/Dropdown';
import useToast from '@/components/Toast/useToast';
import { useEditField } from '@/hooks/useEditField';
import { useUpdatePlaygroundSettingsMutation } from '@/hooks/useSwr';
import { PlaygroundSettingsSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import ModelConfigurationDropdownContent from './ModelConfigurationDropdownContent';
import { DEFAULT_CONFIG_NAME } from './constants';

interface ConfigurationDropdownProps {
  showDropdown: boolean;
  currentConfigId: string | undefined;
  defaultConfigId?: string;
  storedPlaygroundSettings: PlaygroundSettingsSchema[];
  onSelectConfig: (config: PlaygroundSettingsSchema) => void;
}

export const ModelConfigurationDropdown = ({
  showDropdown,
  currentConfigId,
  defaultConfigId,
  storedPlaygroundSettings,
  onSelectConfig,
}: ConfigurationDropdownProps) => {
  const { createToast } = useToast();
  const isSavedConfig = !!currentConfigId;

  const name =
    storedPlaygroundSettings?.find((config) => config.id === currentConfigId)
      ?.name ?? DEFAULT_CONFIG_NAME;
  const [isEditingName, setIsEditingName] = useState(false);
  const [stagedName, setStagedName] = useEditField(name);

  const description =
    storedPlaygroundSettings?.find((config) => config.id === currentConfigId)
      ?.description ?? '';
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [stagedDescription, setStagedDescription] = useEditField(description);

  const updateStoredPlaygroundSettings = useUpdatePlaygroundSettingsMutation(
    currentConfigId ?? '',
    {
      onError: (e) => {
        createToast({
          title: 'Error',
          description: `Unable to update configuration name: ${e.message}`,
          error: true,
        });
        setStagedName(name);
        setStagedDescription(description);
      },
    }
  );

  const handleUpdateSubmit = useCallback(
    (e: FormEvent, name: string, description: string) => {
      e.preventDefault();
      e.stopPropagation();
      updateStoredPlaygroundSettings.trigger({
        json: { name: name, description: description },
      });
    },
    [updateStoredPlaygroundSettings]
  );

  const renderName = () => {
    if (isEditingName) {
      return (
        <Input
          placeholder="Name"
          value={stagedName}
          onChange={(e) => setStagedName(e.target.value)}
          onBlur={(e) => {
            if (stagedName !== name && stagedName !== '') {
              handleUpdateSubmit(e, stagedName, stagedDescription);
            }
            setIsEditingName(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleUpdateSubmit(e, stagedName, stagedDescription);
              setIsEditingName(false);
            }
          }}
          size="sm"
          sx={{
            width: '200px',
          }}
          autoFocus
        />
      );
    }
    return (
      <div
        className="w-[200px] cursor-pointer truncate border-r border-primary px-2 py-1 text-sm hover:bg-primary-hover"
        onClick={() => setIsEditingName(true)}
      >
        {stagedName}
      </div>
    );
  };

  const renderDescription = () => {
    if (!isSavedConfig) return null;
    if (isEditingDescription) {
      return (
        <Input
          placeholder="Description"
          value={stagedDescription}
          onChange={(e) => setStagedDescription(e.target.value)}
          onBlur={(e) => {
            if (stagedDescription !== description) {
              handleUpdateSubmit(e, stagedName, stagedDescription);
            }
            setIsEditingDescription(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleUpdateSubmit(e, stagedName, stagedDescription);
              setIsEditingDescription(false);
            }
          }}
          size="sm"
          sx={{
            width: '200px',
            fontSize: '12px',
          }}
          autoFocus
        />
      );
    }
    return (
      <span
        className="w-[200px] cursor-pointer rounded-md border border-transparent px-1 py-1.5 text-xs text-tertiary hover:border-primary"
        onClick={() => setIsEditingDescription(true)}
      >
        {stagedDescription || 'Description'}
      </span>
    );
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <div className="flex flex-col gap-0.5">
        <div className="flex flex-row items-center rounded-md border border-primary">
          {isSavedConfig && renderName()}
          <DropdownMenuTrigger
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={cn(
              'my-auto flex w-fit cursor-pointer flex-row gap-2 rounded-md px-2 py-1 font-medium transition duration-150 hover:bg-primary',
              !showDropdown ? 'opacity-50' : ''
            )}
            data-testid="dropdown-menu-trigger"
          >
            {!isSavedConfig && (
              <span className="text-xs font-medium">{DEFAULT_CONFIG_NAME}</span>
            )}
            <ChevronDownIcon className="my-auto h-4 w-3" />
          </DropdownMenuTrigger>
        </div>
        {renderDescription()}
      </div>
      <ModelConfigurationDropdownContent
        currentConfigId={currentConfigId}
        defaultConfigId={defaultConfigId}
        storedPlaygroundSettings={storedPlaygroundSettings}
        handleClose={() => setIsDropdownOpen(false)}
        onClick={onSelectConfig}
      />
    </DropdownMenu>
  );
};
